import { combineEpics, Epic, ActionsObservable, StateObservable, ofType } from 'redux-observable';
import { RootAction, RootState } from '@src/app/_redux';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { concat } from 'rxjs/observable/concat';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { actionCreators, SEND_CUSTSERV_EMAIL_START, SEND_EXCEPTION_EMAIL_START } from '@src/app/accounts/customer-service/actions';
import { actionCreators as gaActionCreators } from '@src/app/analytics/googleAnalytics/actions';
import { SendEmailConfig, SendEmailFields } from '@src/app/_global/domain/sesEmailConfig';
import { SendEmailClient } from '@src/app/_global/utility/sendEmailUtils';

const TEST_EMAIL = 'GCTesting@cds-global.com';
const EMAIL_TYPE_CUST_SERV = 'Send Custserv Email';
const EMAIL_TYPE_PAID_CANCEL = 'Cancel Paid Order';

const formatCustServEmailBody = (payload: any)  => {
  let emailBody = '';
  if (payload.fromLogin) {
    emailBody += 'Originated from: Global Care Login Help' + '\n\n';
  } else {
    emailBody += 'Originated from: Global Care' + '\n\n';
  }
  if (payload.customer) {
    emailBody += 'Product Code: ' + payload.selectedProdIdAlias + '\n\n';
    // emailBody += 'Product Code: ' + payload.customer.prodIdAlias + '\n';
  } else {
    emailBody += 'Product: ' + payload.prodIdAlias + '\n';
  }
  if (payload.selectedProductName) {
    emailBody += 'Product/Plan Name: ' + payload.selectedProductName + '\n\n';
  }
  if (payload.customer) {
     emailBody += 'Account Number: ' +  payload.customer.accountNumber + '\n';
  }
  emailBody += 'Customer Email: ' + payload.customerEmailAddress + '\n';
  if (payload.customer) {
    emailBody += 'Name: ' + payload.customer.address.name + '\n';
    emailBody += 'Address 1: ' + payload.customer.address.address1 + '\n';
  }
  if (payload.customer && payload.customer.address.address2.length > 0) {
    emailBody += 'Address 2: ' + payload.customer.address.address2 + '\n';  // hide if empty
  }
  if (payload.customer && payload.customer.address.address3.length > 0) {
    emailBody += 'Address 3: ' + payload.customer.address.address3 + '\n';  // hide if empty
  }
  if (payload.customer) {
    emailBody += 'City/State: ' + payload.customer.address.city + ',' + payload.customer.address.state  +  '\n';
    emailBody += 'ZIP/Postal Code: ' + payload.customer.address.postalCode + '\n';
  }
  emailBody += 'Additional Information: ' + payload.emailContent.emailAdditionalInfo + '\n';
  return emailBody;
};
const formatPaidCancelEmailBody = (payload: any)  => {
  let emailBody = '';
  emailBody += 'Originated from: Global Care' + '\n';
  emailBody += '\rCustomer Info: \n';
  emailBody += 'Product: ' + payload.customer.prodIdAlias + '\n';
  emailBody += 'Account Number: ' + payload.customer.accountNumber + '\n';
  emailBody += 'Cancellation Reason: ' + payload.reason + '\n';
  emailBody += 'Customer Email: ' + payload.customerEmailAddress + '\n';
  emailBody += 'Name: ' + payload.customer.address.name + '\n';
  emailBody += 'Address 1: ' + payload.customer.address.address1 + '\n';
  if (payload.customer.address.address2.length > 0) {
    emailBody += 'Address 2: ' + payload.customer.address.address2 + '\n';  // hide if empty
  }
  if (payload.customer.address.address3.length > 0) {
    emailBody += 'Address 3: ' + payload.customer.address.address3 + '\n';  // hide if empty
  }
  emailBody += 'City/State: ' + payload.customer.address.city + ',' + payload.customer.address.state  +  '\n';
  emailBody += 'Zip/Postal: ' + payload.customer.address.postalCode + '\n';

  emailBody += '\rOrder Info: \n';
  emailBody += 'Order Number: ' + payload.orderNumber + '\n';
  if (payload.invoiceNumber.length > 0) {
    emailBody += 'Invoice Number: ' + payload.invoiceNumber + '\n';
  }
  emailBody += 'Order Date: ' + payload.orderDate + '\n';
  payload.nonGiftOrderItems.forEach(orderItem => {
    emailBody += 'Order Item Description: ' + orderItem.productDesc + '\n';  
    emailBody += 'Order Item Value: $' + orderItem.itemValue + '\n'; 
  });
  if (payload.lastPaymentDate.length > 0) {
    emailBody += 'Last Payment Date: ' + payload.lastPaymentDate + '\n';
  }
  if (payload.lastPaymentAmount.length > 0) {
    emailBody += 'Last Payment Amt: $' + payload.lastPaymentAmount + '\n';
  }
  if (payload.paymentMethod.length > 0) {
    emailBody += 'Last Payment Method: ' + payload.paymentMethod + '\n';
  }
  if (payload.isRecipient) {
    payload.recipOrderItems.forEach(recip => {
      if (recip.isContinuity) {
        emailBody += 'Plan Description: ' + recip.subscriptionName + '\n';  
      } else {
        emailBody += 'Order Item Description: ' + recip.productDesc + '\n';  
      }
    });
  }
  emailBody += '\r Cancel Info: \n';
  if (payload.nonGiftOrderItems.length > 0) { 
    emailBody += 'Non Gift(s): \n'; 
    payload.nonGiftOrderItems.forEach(nonGiftOrderItem => {
      emailBody += '\tName: ' + payload.customer.address.name + '\n';  
      emailBody += '\tAccount Number: ' + nonGiftOrderItem.shipToAccountNumber + '\n';  
      if (nonGiftOrderItem.isContinuity) {
        emailBody += '\tPlan Description: ' + nonGiftOrderItem.subscriptionName + '\n';  
      } else {
        emailBody += '\tOrder Item Description: ' + nonGiftOrderItem.productDesc + '\n';  
      }
      emailBody += '\tOrder Item Value: $' + nonGiftOrderItem.itemValue + '\n'; 
    });
  }
  if ( payload.recipOrderItems.length > 0) { 
    emailBody += 'Gift Recipient(s): \n'; 
    payload.recipOrderItems.forEach(recipOrderItem => {
      emailBody += '\tRecipient Name: ' + (payload.isRecipient ? payload.customer.address.name : recipOrderItem.recipientName ) + '\n';  
      emailBody += '\tRecipient Account Number: ' + recipOrderItem.shipToAccountNumber + '\n';  
      if (recipOrderItem.isContinuity) {
        emailBody += '\tPlan Description: ' + recipOrderItem.subscriptionName + '\n';  
      } else {
        emailBody += '\tOrder Item Description: ' + recipOrderItem.productDesc + '\n';  
      }
      emailBody += '\tOrder Item Value: $' + recipOrderItem.itemValue + '\n'; 
    });
  }

  return emailBody;
};

export const sendCustservEmail =  (
  action$: ActionsObservable<RootAction>,
  state$: StateObservable<RootState>,
) =>
    action$.pipe(
      ofType(SEND_CUSTSERV_EMAIL_START),
      mergeMap(({ payload }) => {
      const emailClient = new SendEmailClient();
      const clientConfig = state$.value.clientConfig.clientConfig;
      const loading = of(actionCreators.sendCustservEmailLoading(true));
      let emailMessage = '';
      switch (payload.emailType) {
        case EMAIL_TYPE_CUST_SERV:
          emailMessage = formatCustServEmailBody(payload);
          break;
        case EMAIL_TYPE_PAID_CANCEL:
          emailMessage = formatPaidCancelEmailBody(payload);
          break;
        default:
          emailMessage = '';
      }
      emailMessage += '\rGC Email Session: ' + clientConfig.uuid;
      
      var sendEmailFields: SendEmailFields = {
        from: payload.custServEmailAddress,
        subject: payload.emailSubject,
        message: emailMessage,
        to: payload.custServEmailAddress,
        replyTo: payload.customerEmailAddress,
        altText: '',
        cc: '',
        bcc: '',
      };
      // console.log('process.env.NODE_ENV : ' + process.env.NODE_ENV);
      if (process.env.NODE_ENV !== 'production') {
        if (!sendEmailFields.from || !sendEmailFields.from.includes('cds-global.com')) {
          sendEmailFields.from = TEST_EMAIL;
        }
        if (!sendEmailFields.to || !sendEmailFields.to.includes('cds-global.com')) {
          sendEmailFields.to = TEST_EMAIL;
        }
        if (!sendEmailFields.replyTo || !sendEmailFields.replyTo.includes('cds-global.com')) {
          sendEmailFields.replyTo = TEST_EMAIL;
        }
      }
      const sendEmailConfig: SendEmailConfig = {
        credentialsObject: state$.value.auth.omsAuth.credentials,
        providerType: clientConfig.providerType,
        emailFields: sendEmailFields,
        region: clientConfig.Region,
        identityPoolId: clientConfig.identityPoolId
      };
      const source$ = fromPromise(emailClient.sendEmail(sendEmailConfig)).pipe(
        map(answer => {
          const isSuccess = answer.$metadata.httpStatusCode === 200;
          let gaCategory = payload.emailType === 'Cancel Paid Order' ? payload.googleAnalyticsCategory : payload.emailType + ': '  + payload.emailSubject;
          if (isSuccess) {
            gaActionCreators.createGAEvent({
              category: gaCategory,
              action: 'Successful',
              label: payload.customer.prodIdAlias
            });
            return actionCreators.sendCustservEmailSuccess({
              success: true,
              response: answer,
              payload: payload
            });
          } else {
            gaActionCreators.createGAEvent({
              category: gaCategory,
              action: 'Unsuccessful',
              label: payload.customer.prodIdAlias
            });
            return actionCreators.sendCustservEmailError({
              success: false,
              message: 'Error ' + payload.emailType,
            });
          }
        }),
        catchError(err => {
          gaActionCreators.createGAEvent({
            category: payload.emailType,
            action: 'Unknown Error',
            label: payload.customer.prodIdAlias
          });
          return of(
            actionCreators.sendCustservEmailError({
              success: false,
              message: err,
            }),
          );
        }),
      );
      return concat(loading, source$);
    }),
  );

export const sendExceptionEmail =  (
  action$: ActionsObservable<RootAction>,
  state$: StateObservable<RootState>,
) =>
    action$.pipe(
      ofType(SEND_EXCEPTION_EMAIL_START),
      mergeMap(({ payload }) => {
      const emailClient = new SendEmailClient();
      const clientConfig = state$.value.clientConfig.clientConfig;
      const loading = of(actionCreators.sendCustservEmailLoading(true));
      let emailMessage = '';
      emailMessage += 'Product: ' + payload[0].prodIdAlias + '\n';
      emailMessage += 'Account Number: ' + payload[0].accountNumber + '\n';

      var sendEmailFields: SendEmailFields = {
        from: payload[0].exceptionProcessingEmailAddr,
        subject: payload[0].emailSubject,
        message: emailMessage,
        to: payload[0].exceptionProcessingEmailAddr,
        replyTo: payload[0].exceptionProcessingEmailAddr,
        altText: '',
        cc: '',
        bcc: '',
      };
      // console.log('process.env.NODE_ENV : ' + process.env.NODE_ENV);
      if (process.env.NODE_ENV !== 'production') {
        if (!sendEmailFields.from || !sendEmailFields.from.includes('cds-global.com')) {
          sendEmailFields.from = TEST_EMAIL;
        }
        if (!sendEmailFields.to || !sendEmailFields.to.includes('cds-global.com')) {
          sendEmailFields.to = TEST_EMAIL;
        }
        if (!sendEmailFields.replyTo || !sendEmailFields.replyTo.includes('cds-global.com')) {
          sendEmailFields.replyTo = TEST_EMAIL;
        }
      }
      const sendEmailConfig: SendEmailConfig = {
        credentialsObject: state$.value.auth.omsAuth.credentials,
        providerType: clientConfig.providerType,
        emailFields: sendEmailFields,
        region: clientConfig.Region,
        identityPoolId: clientConfig.identityPoolId
      };
      const source$ = fromPromise(emailClient.sendEmail(sendEmailConfig)).pipe(
        map(answer => {
          const isSuccess = answer.$metadata.httpStatusCode === 200;
          if (isSuccess) {
            gaActionCreators.createGAEvent({
              category: payload[0].emailType + ' : '  + payload.emailSubject,
              action: 'Successful',
              label: payload[0].prodIdAlias
            });
            return actionCreators.sendExceptionEmailSuccess({
              success: true,
              response: answer,
            });
          } else {
            gaActionCreators.createGAEvent({
              category: payload[0].emailType + ' : '  + payload.emailSubject,
              action: 'Unsuccessful' ,
              label: payload[0].prodIdAlias
            });
            return actionCreators.sendExceptionEmailError({
              success: false,
              message: 'Error ',
            });
          }
        }),
        catchError(err => {
          gaActionCreators.createGAEvent({
            category: payload[0].emailType + ' : '  + payload.emailSubject,
            action: 'Unknown Error',
            label: payload[0].prodIdAlias
          });
          return of(
            actionCreators.sendExceptionEmailError({
              success: false,
              message: err,
            }),
          );
        }),
      );
      return concat(loading, source$);
    }),
  );
export const epics = combineEpics(sendCustservEmail, sendExceptionEmail);
